import { EventEmitter, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { PlatformService } from '@panamax/app-state';
import { MAX_STATEMENT_SELECTION } from '@payments/constants/constants';
import { ARStatementsPageViewModel } from '@payments/models/ar-statements-view.model';
import { PaymentFilter } from '@payments/pages/payment-activity/services/payment-activity.service';
import { DashboardFilter } from '@payments/pages/payment-dashboard/services/payment-dashboard.service';
import {
  getArStatementFiltersAndSortInformation,
  getARStatementsPageViewModel,
} from '@payments/selectors/payments.selector';
import { FileHandlerPaymentsService } from '@payments/services/file-handler-payments.service';
import { ModalFilterService } from '@payments/services/payments-modal.service';
import { PaymentsService } from '@payments/services/payments.service';
import { PaymentsAnalyticsMessages } from '@payments/tracking/analytics/payments-analytics.constants';
import { PaymentsAnalyticsService } from '@payments/tracking/analytics/payments-analytics.service';
import { ServiceHandlerService } from '@shared/services/service-handler.service';
import {
  arStatementSelectors,
  ArStatementStoreService,
  Filter,
  InvoiceFilterIds,
  SortDirection,
  SortFilter,
} from '@usf/ngrx-payment';
import {
  ARDocFormat,
  DownloadARStatementsResponse,
  Status,
} from '@usf/payment-types';
import dayjs from 'dayjs';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilKeyChanged,
  filter,
  first,
  map,
  Observable,
  of,
  Subject,
  take,
  takeUntil,
  throwError,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { ARStatementsAnalyticsMessages } from '../tracking/analytics/ar-statements-analytics.constants';

export enum ArStatementFilterIds {
  customers = 'customers',
  fromStatementDate = 'fromStatementDate',
  statementDate = 'statementDate',
  toStatementDate = 'toStatementDate',
}

export enum ArStatementSortFields {
  customerName = 'customerName',
  statementDate = 'statementDate',
}

@Injectable({
  providedIn: 'root',
})
export class ArStatementsService implements ModalFilterService {
  readonly defaultSort = [
    {
      fieldName: ArStatementSortFields.statementDate,
      sortValue: SortDirection.DESC,
    },
    {
      fieldName: ArStatementSortFields.customerName,
      sortValue: SortDirection.ASC,
    },
  ];
  isInitialLoading$ = new BehaviorSubject<boolean>(true);

  constructor(
    private store: Store,
    readonly platformService: PlatformService,
    private arStatementStoreService: ArStatementStoreService,
    private serviceHandler: ServiceHandlerService,
    readonly paymentsService: PaymentsService,
    private fileHandlerService: FileHandlerPaymentsService,
    private translateService: TranslateService,
    private paymentsAnalyticsService: PaymentsAnalyticsService,
  ) {}

  getARStatementsViewModel$(): Observable<ARStatementsPageViewModel> {
    return combineLatest([
      this.store.select(getARStatementsPageViewModel),
      this.isInitialLoading$,
    ]).pipe(
      map(([vm, isInitialLoading]) => {
        if (!vm.isLoading && isInitialLoading) {
          setTimeout(() => {
            this.isInitialLoading$.next(false);
          }, 100);
        }
        return {
          ...vm,
          isInitialLoading,
        };
      }),
    );
  }

  setInitialArStatementsSortAndFilter(initialFilters: Filter[] = []) {
    const now = new Date();
    const todaysDate = this.paymentsService.formatDate(now);
    const sixMonthsAgo = this.paymentsService.formatDate(
      dayjs(now).subtract(6, 'month').toDate(),
    );
    const minDate = this.paymentsService.formatDate(
      dayjs.utc('2025-02-03').toDate(),
    );

    const statementDatefilters: Filter[] = [
      {
        filterName: ArStatementFilterIds.fromStatementDate,
        value: sixMonthsAgo > minDate ? sixMonthsAgo : minDate,
      },
      {
        filterName: ArStatementFilterIds.toStatementDate,
        value: todaysDate,
      },
    ];
    const filters = [...initialFilters, ...statementDatefilters].filter(
      (filter, index, array) =>
        array.findIndex(f => f.filterName === filter.filterName) === index,
    );

    const includeCustomerFilter =
      filters.findIndex(
        filter => filter.filterName === InvoiceFilterIds.Customers,
      ) >= 0;

    if (includeCustomerFilter) {
      this.arStatementStoreService.setArStatementFilters(filters);
      this.arStatementStoreService.setArStatementSort(this.defaultSort);
      return of(null);
    } else {
      return this.paymentsService
        .getSelectedPaymentCustomers()
        .pipe(
          filter(paymentCustomer => paymentCustomer.customers?.length > 0),
          take(1),
        )
        .subscribe(paymentCustomer => {
          const filtersWithCustomers = [
            ...filters,
            {
              filterName: ArStatementFilterIds.customers,
              value: paymentCustomer.customers,
            } as Filter,
          ];
          this.arStatementStoreService.setArStatementFilters(
            filtersWithCustomers,
          );
          this.arStatementStoreService.setArStatementSort(this.defaultSort);
        });
    }
  }

  // ModalFilterService

  getFiltersAndSortInformation$(): Observable<{
    filters: Filter[];
    sort: SortFilter[];
  }> {
    return this.store.select(getArStatementFiltersAndSortInformation);
  }

  getStatusOptions(): Map<Status, string> {
    return new Map([]);
  }

  setFilter(
    statementFilters: DashboardFilter[] | Filter[] | PaymentFilter[],
    _restrictToOpen?: boolean,
  ): void {
    this.clearSelectedStatements();
    this.arStatementStoreService.setArStatementFilters(statementFilters);
  }

  setSort(sortOrder: SortFilter[]) {
    if (sortOrder[0]?.fieldName === ArStatementSortFields.statementDate) {
      sortOrder.push({
        fieldName: ArStatementSortFields.customerName,
        sortValue: SortDirection.ASC,
      });
    }
    this.arStatementStoreService.setArStatementSort(sortOrder);
  }

  clearFiltersAndResetSort() {
    this.arStatementStoreService.setArStatementFilters([]);
    this.arStatementStoreService.setArStatementSort(this.defaultSort);
  }

  async showDownloadFormatModal(arStatementsID: string[]) {
    const isTouch = this.platformService.isTouch.value;
    const downloadStatementsEvent$: EventEmitter<ARDocFormat> =
      new EventEmitter();
    downloadStatementsEvent$.pipe(take(1)).subscribe(format => {
      this.downloadStatements(arStatementsID, format);
    });

    return this.fileHandlerService.chooseFormatDownloadModal(
      downloadStatementsEvent$,
      [
        {
          format: ARDocFormat.PDF,
          title: 'i18n.payments.arStatements.statementDownload.pdfTitle',
          helpText: '',
        },
        {
          format: ARDocFormat.CSV,
          title: 'i18n.payments.arStatements.statementDownload.csvTitle',
          helpText: '',
        },
      ],
      isTouch,
    );
  }

  selectStatement(
    arStatementId: string,
    maxTransactionsSelectedMessage: string,
  ) {
    this.store
      .select(arStatementSelectors.selectArStatements)
      .pipe(first())
      .subscribe(statements => {
        const isUnselecting = statements.selectedIds.includes(arStatementId);
        const selectionCount = statements.selectedIds.length;

        if (!isUnselecting && selectionCount >= MAX_STATEMENT_SELECTION) {
          this.paymentsService.showToastMessage(maxTransactionsSelectedMessage);
        } else {
          this.arStatementStoreService.selectArStatements([arStatementId]);
        }
      });
  }

  selectMaxStatements(
    maxTransactionsSelectedMessage: string,
    remainingQuantityToSelect?: number,
  ) {
    if (remainingQuantityToSelect <= 0) {
      this.paymentsService.showToastMessage(maxTransactionsSelectedMessage);
      return;
    }

    remainingQuantityToSelect =
      remainingQuantityToSelect || MAX_STATEMENT_SELECTION;
    const subscription = new Subject<boolean>();
    this.store
      .select(arStatementSelectors.selectArStatements)
      .pipe(
        takeUntil(subscription),
        filter(state => !state.loading),
        distinctUntilKeyChanged('ids'),
      )
      .subscribe(arStatementsState => {
        const statementCount = arStatementsState.ids.length;
        if (
          statementCount < remainingQuantityToSelect &&
          arStatementsState.nextPage
        ) {
          this.fetchStatements(1, MAX_STATEMENT_SELECTION);
        } else {
          const selectedCount = arStatementsState.selectedIds?.length ?? 0;
          let willSelectCount = Math.min(
            statementCount - selectedCount,
            remainingQuantityToSelect,
          );
          this.arStatementStoreService.selectMaxStatements(willSelectCount);

          if (selectedCount + willSelectCount >= MAX_STATEMENT_SELECTION) {
            this.paymentsService.showToastMessage(
              maxTransactionsSelectedMessage,
            );
          }
          subscription.next(true);
          subscription.complete();
        }
      });
  }

  fetchStatements(pageNum: number, pageSize?: number) {
    this.arStatementStoreService.fetchArStatements(pageNum, pageSize);
  }

  clearSelectedStatements() {
    this.arStatementStoreService.clearSelectedArStatements();
  }

  fetchStatementsFiles(
    ids: string[],
    documentFormat = ARDocFormat.PDF,
  ): Observable<DownloadARStatementsResponse> {
    const body = {
      docIDs: ids,
      docFormat: documentFormat.toUpperCase(),
    };
    return this.serviceHandler
      .post<DownloadARStatementsResponse>(
        `${environment.paymentArStatementsApiUrl}/downloadARStatements`,
        body,
      )
      .pipe(
        take(1),
        filter(res => !!res),
        catchError(error => throwError(() => error)),
      );
  }

  shareStatement(statementID: string) {
    this.fetchStatementsFiles([statementID]).subscribe(res => {
      const statementTitle = this.translateService.instant(
        'i18n.payments.arStatements.statement',
      );
      const { contentType } = this.fileHandlerService.handleFormat(
        res.docFormat,
        statementTitle,
      );
      const blob = this.fileHandlerService.base64ToByteArrayToBlob(
        res.docData,
        contentType,
      );
      const shareOptions = { title: statementTitle };
      this.fileHandlerService.shareFileNative(
        blob,
        statementTitle,
        '.pdf',
        shareOptions,
      );
    });
  }

  downloadStatements(
    arStatementsID: string[],
    format: ARDocFormat,
    isView: boolean = false,
  ) {
    this.fetchStatementsFiles(arStatementsID, format).subscribe(
      res => {
        this.fileHandlerService.downloadAction(
          res,
          isView,
          'arStatements.viewStatementError',
          res.docFormat === ARDocFormat.ZIP ? 'Statements' : arStatementsID[0],
        );
        !isView
          ? this.paymentsAnalyticsService.trackDownload(
              arStatementsID.length,
              format,
              ARStatementsAnalyticsMessages.fileTypeBase,
            )
          : this.paymentsAnalyticsService.trackView(
              PaymentsAnalyticsMessages.viewStatementsPageName,
            );
      },
      _e => {
        this.paymentsService.showToastMessage(
          this.translateService.instant(
            'i18n.payments.arStatements.generalErrorMsg',
          ),
        );
      },
    );
  }
}
